/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import FormBox from '../../common/FormBox/FormBox';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formCounterSlice } from '../../../redux/formCounter';
import api from 'api';
import { VisaPurposeServerValues, VisaPurposes } from '../../../data/constants/constants';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import BaseInput from 'components/common/BaseInput';
import PasswordInput from 'components/common/PasswordInput';
import PhoneNumberField from 'components/common/PhoneNumberField/PhoneNumberField';
import {
    CircularProgress,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Tooltip,
} from '@mui/material';
import { RootState } from 'redux/store';
import ReactGA from 'react-ga4';
import * as S from '../Forms.styled';
import { useAuth } from 'contexts/ProtectedRoute';
import { useParams } from 'react-router-dom';
import { isSalesPersonRole } from 'data/constants/roles';
import { useTranslation } from 'react-i18next';
import { fetchOptions } from './utils';
import UploadCVButton from './UploadCVButton';
import { isValidPassword } from 'utils/validations/password';
import PasswordStrengthIndicator from 'components/common/PasswordStrengthIndicator/PasswordStrengthIndicator';
import { ProfileEditContext } from 'views/Dashboards-B2B/components/BACKOFFICEFLOW/CLIENTVIEW/components/ClientProfile/components/ProfileEditContext';
import { toPurposeServerValue } from 'helpers/purpose';
import { isProfileOnlySpouseSelected } from 'utils/selectedAccount';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    slotProps: {
        paper: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    },
};

export interface Spouse {
    firstname: string;
    lastname: string;
    middlename: string;
    birth_date: string;
    gender: string;
    nationality: string;
    country_of_residence: string;
    region_of_residence: string;
    residency_status: string;
    residence_since: string;
}

interface Entry {
    purpose: string;
}

export const entryUser: Entry = {
    purpose: '',
};

interface ValidationMsgP {
    purpose: string;
}

const initValidationMsgP: ValidationMsgP = {
    purpose: '',
};

const purposeOptions: string[] = ['Work', 'Business', 'Study', 'Travel', 'All'];

const formEntries = [
    'firstname',
    'lastname',
    'email',
    'phone_number',
    'password',
    'repeatPassword',
];

export interface UserData {
    firstname: string;
    lastname: string;
    email: string;
    phone_number: string;
    password: string;
    repeatPassword: string;
}

export interface ValidationMsgUser {
    firstname: string;
    lastname: string;
    email: string;
    phone_number: string;
    password: string;
    repeatPassword: string;
}

export const initUser: UserData = {
    firstname: '',
    lastname: '',
    email: '',
    phone_number: '',
    password: '',
    repeatPassword: '',
};

export interface CitizenshipInfoData {
    country: string | null;
    user_id: number | null;
}

interface ValidationMsgPI {
    country: string;
    user_id: number | null;
}

const initValidationMsgPI: ValidationMsgPI = {
    country: '',
    user_id: null,
};

const initialStatePI: CitizenshipInfoData = {
    country: '',
    user_id: null,
};

export default function GeneralInfo() {
    const {
        id: currentUserId,
        language_setting: language,
        spouse,
    } = useSelector((state: RootState) => state.user.entity);
    const selectedUserId = useSelector((state: RootState) => state.selectedAccount.selectedUserId);
    const { t } = useTranslation();
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const profileEdit = useContext(ProfileEditContext);
    const isEmailOptional =
        profileEdit?.isEmailOptional === true ||
        isProfileOnlySpouseSelected(currentUserId, spouse, selectedUserId);
    const [userData, setUserData] = useState<UserData>(initUser);
    const [isUserData, setIsUserData] = useState<boolean>(false);
    const [citizenshipInfo, setCitizenshipInfo] = useState<string>('');
    const [countryOptions, setCountryOptions] = useState<string[]>([]);
    const [convertedCitizenshipInfo, setConvertedCitizenshipInfo] = useState<any>();
    const [isPersonalInfo, setIsPeresonalInfo] = useState<boolean>(false);
    const [validationMsgUser, setValidationMsgUser] = useState<ValidationMsgUser>(initUser);
    const [validationMsgPI, setValidationMsgPI] = useState<ValidationMsgPI>(initValidationMsgPI);
    const [purposeValue, setPurposeValue] = useState<Entry>(entryUser);
    const { purpose } = useSelector((state: RootState) => state.formCounter);
    const { setPurpose } = formCounterSlice.actions;
    const [isPurposeData, setIsPurposeData] = useState<boolean>(false);
    const dispatch = useDispatch();
    const [validationMsgPurpose, setValidationMsgPurpose] =
        useState<ValidationMsgP>(initValidationMsgP);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        if (citizenshipInfo) {
            const data = convertData(citizenshipInfo);
            setConvertedCitizenshipInfo(data);
        }
    }, [citizenshipInfo]);

    const getCountryOptions = () =>
        fetchOptions(api?.assessment_options.getCountryOptions, setCountryOptions);

    const convertData = (data: string) => {
        if (data) {
            return data.split(', ').map((country: string) => ({ country }));
        }
    };

    useEffect(() => {
        if (isUserData && isPersonalInfo && isPurposeData) {
            setIsLoading(false);
        }
    }, [isUserData, isPersonalInfo, isPurposeData]);

    useEffect(() => {
        if (!purpose) {
            setPurposeValue(entryUser);
            return;
        }
        const tempVal =
            VisaPurposeServerValues[formatEnum(purpose) as keyof typeof VisaPurposeServerValues];
        setPurposeValue({ purpose: toPurposeServerValue(tempVal || purpose) });
    }, [purpose]);

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname}`,
            title: 'GeneralInfo',
        });
    }, []);

    const loadUserData = (data: UserData) => {
        if (data && Object.keys(data).length) {
            data = { ...data, password: '', repeatPassword: '' };
            setUserData(data);
        }
        if (data == null || data) {
            setIsUserData(true);
        } else {
            setIsUserData(false);
        }
    };

    const loadCitizenshipInfo = async (data: CitizenshipInfoData[]) => {
        getCountryOptions();
        if (data && Object.keys(data).length) {
            const countriesString = data
                .filter((item) => item.country !== null)
                .map((item) => item.country)
                .join(', ');
            setCitizenshipInfo(countriesString);
        }
        if (data == null || data) {
            setIsPeresonalInfo(true);
        } else {
            setIsPeresonalInfo(false);
        }
    };

    const loadData = (data: Entry) => {
        if (data?.purpose) {
            const indexOfS = Object.values(VisaPurposeServerValues).indexOf(
                data.purpose as unknown as VisaPurposeServerValues
            );
            if (indexOfS >= 0) {
                const key = Object.keys(VisaPurposeServerValues)[indexOfS];
                dispatch(setPurpose(key));
            } else {
                dispatch(setPurpose(''));
            }
            setPurposeValue({ purpose: toPurposeServerValue(data.purpose) });
        } else {
            dispatch(setPurpose(''));
            setPurposeValue(entryUser);
        }
        setIsPurposeData(true);
    };

    //temp path change

    // const [hasFamily, setHasFamily] = useState<string>('No');
    // const setFamilyData = async (data: any) => {
    //     if (data && data.length | Object.keys(data).length) {
    //         if (data[0].firstname !== null) {
    //             setHasFamily('Yes');
    //         }
    //     }
    // };

    // useEffect(() => {
    //     window.localStorage.setItem('hasFamily', JSON.stringify(hasFamily));
    //     window.dispatchEvent(new Event('storage'));
    // }, [hasFamily]);

    const { role } = useAuth();
    const params = useParams();
    const candidate_id = Number(params.id);

    const isSalesPerson = isSalesPersonRole(role);

    useEffect(() => {
        if (isAuthenticated) {
            setIsLoading(true);
            setIsUserData(false);
            setIsPeresonalInfo(false);
            setIsPurposeData(false);
            setUserData(initUser);
            setCitizenshipInfo('');
            setPurposeValue(entryUser);
            dispatch(setPurpose(''));
            if (isSalesPerson) {
                api.tableInput.fetchCandidateInputSales(loadUserData, 'User', candidate_id);
                api.tableInput.fetchCandidateInputSales(
                    loadCitizenshipInfo,
                    'Citizenship',
                    candidate_id
                );
                api.tableInput.fetchCandidateInputSales(loadData, 'Purpose', candidate_id);
                // api.tableInput.fetchCandidateInputSales(
                //     setFamilyData,
                //     'FamilyMember',
                //     candidate_id
                // );
            } else {
                api.tableInput.fetchCandidateInput(loadUserData, 'User');
                api.tableInput.fetchCandidateInputSales(loadCitizenshipInfo, 'Citizenship');
                //has to be changed
                // api.tableInput.fetchCandidateInput(loadCitizenshipInfo, 'PersonalInfo');
                api.tableInput.fetchCandidateInput(loadData, 'Purpose');
                //  api.tableInput.fetchCandidateInput(setFamilyData, 'FamilyMember');
            }
        } else {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, candidate_id]);

    // FIELD VALIDATION
    const validate = async (field: string, value: string, section?: string) => {
        let validated = true;
        //userData
        if (!section) {
            let temp_userMsg = validationMsgUser;
            if (!field && !value) {
                for (const key of Object.keys(initUser)) {
                    const value = userData[key as keyof UserData];
                    const index = formEntries.findIndex((x) => x === key);
                    if (index === -1) continue;
                    let msg = '';
                    if ((key === 'password' || key === 'repeatPassword') && isAuthenticated) {
                        break;
                    } else if (key === 'email') {
                        if (!value && isEmailOptional) {
                            temp_userMsg = { ...temp_userMsg, [key]: '' };
                        } else if (!validateEmail(key, value)) {
                            msg = t('invalidEmailAddress', { lng: language });
                            temp_userMsg = { ...temp_userMsg, [key]: msg };
                            setValidationMsgUser(temp_userMsg);
                            validated = false;
                        }
                        if (!isAuthenticated && value)
                            try {
                                await api.checkEmail.get(value);
                            } catch {
                                msg = t('emailAlreadyInUse', { lng: language });
                                temp_userMsg = { ...temp_userMsg, [key]: msg };
                                setValidationMsgUser(temp_userMsg);
                                validated = false;
                            }
                    } else if (value === '' || value === null) {
                        msg = t('requiredField', { lng: language });
                        validated = false;
                        temp_userMsg = { ...temp_userMsg, [key]: msg };
                    }
                }
            } else {
                let msg = '';
                if (field === 'firstname' && (value === '' || value === null)) {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                    temp_userMsg = { ...temp_userMsg, [field]: msg };
                } else if (
                    (value === '' || value === null) &&
                    !(field === 'email' && isEmailOptional)
                ) {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                }
                temp_userMsg = { ...temp_userMsg, [field]: msg };
            }
            setValidationMsgUser(temp_userMsg);
        }
        //personalInfo
        if (!section) {
            let tempMsgPI = validationMsgPI;
            if (!field && !value) {
                for (const key of Object.keys(initialStatePI)) {
                    //const value = personalInfo[key as keyof PersonalInfoData];
                    const value = citizenshipInfo;
                    let msg = '';
                    if (key === 'country' && (value === '' || value === null)) {
                        msg = t('requiredField', { lng: language });
                        validated = false;
                        tempMsgPI = { ...tempMsgPI, country: msg };
                    }
                }
            } else {
                let msg = '';
                if (field === 'country') {
                    if (value === '' || value === null) {
                        msg = t('requiredField', { lng: language });
                        validated = false;
                        tempMsgPI = { ...tempMsgPI, country: msg };
                    } else {
                        tempMsgPI = { ...tempMsgPI, country: msg };
                    }
                }
            }
            setValidationMsgPI(tempMsgPI);
        }

        //purpose
        if (!section) {
            let tempMsgPI = validationMsgPurpose;
            if (!field && !value) {
                for (const key of Object.keys(initValidationMsgP)) {
                    if (key === 'purpose' && (purpose === '' || purpose === null)) {
                        const msg = t('requiredField', { lng: language });
                        tempMsgPI = { ...tempMsgPI, purpose: msg };
                        validated = false;
                    }
                }
            } else {
                let msg = '';
                if (field === 'purpose') {
                    if (value === '' || value === null) {
                        msg = t('requiredField', { lng: language });
                        validated = false;
                        tempMsgPI = { ...tempMsgPI, purpose: msg };
                    } else {
                        tempMsgPI = { ...tempMsgPI, purpose: msg };
                    }
                }
            }
            setValidationMsgPurpose(tempMsgPI);
        }
        return validated;
    };
    const validateEmail = (field: string, value: string) => {
        handleUpdateUser(field, value);
        let validated = true;
        let msg = '';
        let tmp = validationMsgUser;

        if (!value && !isEmailOptional) {
            msg = t('requiredField', { lng: language });
            validated = false;
        } else if (value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
            msg = t('invalidEmailAddress', { lng: language });
            validated = false;
        }
        tmp = { ...tmp, [field]: msg };
        setValidationMsgUser(tmp);
        return validated;
    };

    // CANDIDATE update
    const handleUpdateUser = (field: string, value: string) => {
        if (field !== 'password' && field !== 'repeatPassword') {
            validate(field, value);
        }
        const temp: UserData = { ...userData, [field]: value };
        setUserData(temp);
    };

    const handlePassword = (field: string, value: string) => {
        handleUpdateUser(field, value);

        // Password requirements are surfaced by the strength indicator, so we no
        // longer show the policy text as a field error.
        const tmp = { ...validationMsgUser, [field]: '' };
        setValidationMsgUser(tmp);

        return isValidPassword(value);
    };

    const handleConfirmPassword = (field: string, value: string) => {
        handleUpdateUser(field, value);
        let validated = true;
        let tmp = validationMsgUser;

        let msg = '';
        if (userData.password !== value) {
            msg = t('passwordsDoNotMatch', { lng: language });
            validated = false;
        }
        tmp = { ...tmp, [field]: msg };

        setValidationMsgUser(tmp);

        return validated;
    };

    const handleUpdatePurpose = async (value: string | any) => {
        validate('purpose', value);
        if (value === 'All') {
            dispatch(
                setPurpose(VisaPurposes[formatEnum('No Preference') as keyof typeof VisaPurposes])
            );
        } else {
            dispatch(setPurpose(VisaPurposes[formatEnum(value) as keyof typeof VisaPurposes]));
        }
    };

    function formatEnum(value: string | null) {
        const formattedEnum = value?.toUpperCase().replace(/\s/g, '');
        return formattedEnum;
    }

    const [isCitizenshipOpen, setIsCitizenshipOpen] = useState<boolean>(false);
    const handleSecondaryChange = (event: SelectChangeEvent<string[]>) => {
        const {
            target: { value },
        } = event;

        const countriesString = convertToString(value);
        handleNationalityChange(countriesString);
        setIsCitizenshipOpen(false);
    };
    function convertToString(input: string | string[]): string {
        if (Array.isArray(input)) {
            return input.join(', ');
        } else {
            return input;
        }
    }
    // Handler to update purpose state as a comma-separated string
    const handleNationalityChange = (newValues: string) => {
        validate('country', newValues);
        setCitizenshipInfo(newValues);
    };

    const [nationalities, setNationalities] = useState<string[]>([]);

    const getNationalityOptions = () =>
        fetchOptions(api?.assessment_options.getNationalityOptions, setNationalities);

    useEffect(() => {
        getNationalityOptions();
    }, []);

    const nationalityOptions = nationalities.length > 0 ? nationalities : countryOptions;
    const userPayload: Partial<UserData> = { ...userData };
    if (isEmailOptional && !userData.email) {
        delete userPayload.email;
    }

    return (
        <>
            {isLoading ? (
                <S.LoadingContainer>
                    <CircularProgress color="primary" />
                </S.LoadingContainer>
            ) : (
                <FormBox
                    validation={validate}
                    tableNames={['User', 'Citizenship', 'Purpose']}
                    data={[userPayload, convertedCitizenshipInfo, purposeValue]}
                >
                    <S.TitleCVContainer>
                        <S.Title>{t('personalInfo', { lng: language })}</S.Title>
                        <UploadCVButton />
                    </S.TitleCVContainer>

                    <S.FormContainer>
                        <S.GridSection>
                            <Autocomplete
                                sx={{
                                    '& .MuiInputBase-input': {
                                        height: 30,
                                    },
                                }}
                                disablePortal
                                value={purpose}
                                id="purpose"
                                options={purposeOptions}
                                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                    validate('purpose', e.target.value);
                                }}
                                onChange={(_event, value) => {
                                    handleUpdatePurpose(value);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('selectVisaPreference', { lng: language })}
                                        error={validationMsgPurpose.purpose !== ''}
                                        helperText={validationMsgPurpose.purpose}
                                    />
                                )}
                            />

                            <FormControl>
                                <InputLabel id="demo-multiple-name-label">
                                    {`*${t('selectCitizenship', { lng: language })}`}
                                </InputLabel>
                                <Select
                                    labelId="demo-multiple-name-label"
                                    id="demo-multiple-name"
                                    multiple
                                    open={isCitizenshipOpen}
                                    onOpen={() => setIsCitizenshipOpen(true)}
                                    onClose={() => setIsCitizenshipOpen(false)}
                                    value={citizenshipInfo ? citizenshipInfo?.split(', ') : []}
                                    onChange={handleSecondaryChange}
                                    renderValue={(selected) =>
                                        [...new Set(selected as string[])].join(', ')
                                    }
                                    input={
                                        <OutlinedInput
                                            label={`*${t('selectCitizenship', {
                                                lng: language,
                                            })}`}
                                        />
                                    }
                                    MenuProps={MenuProps}
                                    sx={{
                                        width: '100%',
                                        height: '63px',
                                    }}
                                >
                                    {nationalityOptions.map((name: string, j: number) => (
                                        <MenuItem key={j} value={name}>
                                            {name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {validationMsgPI.country && (
                                    <FormHelperText sx={{ color: 'red' }}>
                                        {validationMsgPI.country}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </S.GridSection>
                        <S.GridSection>
                            <BaseInput
                                id="firstname"
                                label={`*${t('firstName', { lng: language })}`}
                                type="text"
                                value={userData.firstname}
                                onFocus={(e) => {
                                    validate('firstname', e.target.value);
                                }}
                                onChange={(e) => {
                                    handleUpdateUser('firstname', e.target.value);
                                }}
                                error={validationMsgUser.firstname !== ''}
                                helperText={validationMsgUser.firstname}
                            />

                            <BaseInput
                                id="lastname"
                                label={`*${t('lastName', { lng: language })}`}
                                type="text"
                                value={userData.lastname}
                                onFocus={(e) => {
                                    validate('lastname', e.target.value);
                                }}
                                onChange={(e) => {
                                    handleUpdateUser('lastname', e.target.value);
                                }}
                                error={validationMsgUser.lastname !== ''}
                                helperText={validationMsgUser.lastname}
                            />
                        </S.GridSection>

                        <S.GridSection>
                            <BaseInput
                                id="email"
                                label={`${isEmailOptional ? '' : '*'}${t('email', {
                                    lng: language,
                                })}`}
                                type="text"
                                value={userData.email}
                                onFocus={(e) => {
                                    validate('email', e.target.value);
                                }}
                                onChange={(e) => {
                                    handleUpdateUser('email', e.target.value);
                                }}
                                error={validationMsgUser.email !== ''}
                                helperText={validationMsgUser.email}
                            />
                            <Tooltip title="Add or select area code from dropdown" followCursor>
                                <PhoneNumberField
                                    sx={{
                                        '&.Mui-error': {
                                            '.MuiOutlinedInput-notchedOutline ': {
                                                borderColor: '#ff2727',
                                            },
                                        },
                                    }}
                                    label={`*${t('phone', { lng: language })}`}
                                    fullWidth
                                    value={userData.phone_number ? userData.phone_number : ''}
                                    onFocus={(e) => {
                                        validate('phone_number', e.target.value);
                                    }}
                                    onChange={(v: string) => {
                                        handleUpdateUser('phone_number', v);
                                    }}
                                    error={validationMsgUser.phone_number !== ''}
                                    helperText={validationMsgUser.phone_number}
                                />
                            </Tooltip>
                        </S.GridSection>
                        {!isAuthenticated && (
                            <S.PasswordGridSection>
                                <PasswordInput
                                    label={`*${t('password', { lng: language })}`}
                                    InputProps={{
                                        inputProps: { autoComplete: 'off' },
                                    }}
                                    fullWidth
                                    value={userData.password}
                                    onFocus={(e) => {
                                        handlePassword('password', e.target.value);
                                    }}
                                    onChange={(
                                        v: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                    ) => {
                                        handlePassword('password', v.target.value);
                                    }}
                                    error={validationMsgUser.password !== ''}
                                    helperText={validationMsgUser.password}
                                />

                                <PasswordStrengthIndicator
                                    password={userData.password}
                                    lng={language}
                                />

                                <PasswordInput
                                    label={`*${t('confirmPassword', { lng: language })}`}
                                    InputProps={{
                                        inputProps: { autoComplete: 'off' },
                                    }}
                                    fullWidth
                                    value={userData.repeatPassword}
                                    onFocus={(e) => {
                                        handleConfirmPassword('repeatPassword', e.target.value);
                                    }}
                                    onChange={(
                                        v: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                    ) => {
                                        handleConfirmPassword('repeatPassword', v.target.value);
                                    }}
                                    error={validationMsgUser.repeatPassword !== ''}
                                    helperText={validationMsgUser.repeatPassword}
                                />
                            </S.PasswordGridSection>
                        )}
                    </S.FormContainer>
                    <S.ContentDivider style={{ marginBottom: '40px !important' }} />
                </FormBox>
            )}
        </>
    );
}
